import React, { useEffect, useState } from "react";
import { ExternalLink, BookOpen, Scale } from "lucide-react";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function Knowledge() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isMgr = ["platform_admin", "supervisor", "management"].includes(user?.role);
  const [form, setForm] = useState({ reference: "", title_ar: "", committee: "", year: "", type: "substantive", basis: "", summary_ar: "", url: "" });
  const submitDecision = async () => {
    try { await api.post("/knowledge/decisions", form); setForm({ reference: "", title_ar: "", committee: "", year: "", type: "substantive", basis: "", summary_ar: "", url: "" }); loadDecisions(); }
    catch (e) { /* ignore */ }
  };
  const [q, setQ] = useState("");
  const [tax, setTax] = useState("all");
  const [committee, setCommittee] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [data, setData] = useState({ decisions: [], committees: [], years: [] });
  const [articles, setArticles] = useState([]);

  const loadDecisions = () => {
    const params = {};
    if (q) params.q = q;
    if (tax !== "all") params.tax = tax;
    if (committee !== "all") params.committee = committee;
    if (dtype !== "all") params.dtype = dtype;
    api.get("/knowledge/decisions", { params }).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { loadDecisions(); /* eslint-disable-next-line */ }, [q, tax, committee, dtype]);
  useEffect(() => { api.get("/knowledge/articles").then((r) => setArticles(r.data.articles)).catch(() => {}); }, []);

  return (
    <div className="space-y-5 gc-fade-up" data-testid="knowledge-center">
      <div>
        <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("knowledge_center")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("kn_disclaimer")}</p>
      </div>

      <Tabs defaultValue="decisions">
        <TabsList>
          <TabsTrigger value="decisions" data-testid="kn-tab-decisions"><Scale className="h-4 w-4 me-1.5" />{t("decisions_tab")}</TabsTrigger>
          <TabsTrigger value="articles" data-testid="kn-tab-articles"><BookOpen className="h-4 w-4 me-1.5" />{t("articles_tab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="decisions" className="mt-4 space-y-4">
          {isMgr && (
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="add-decision-form">
              <div className="text-sm font-semibold text-[hsl(217_63%_15%)] mb-3">{t("add_decision")}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input data-testid="kn-f-reference" placeholder="VT-2025-000000" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                <Input data-testid="kn-f-committee" placeholder={lang === "ar" ? "الدائرة" : "Committee"} value={form.committee} onChange={(e) => setForm({ ...form, committee: e.target.value })} />
                <Input data-testid="kn-f-title" placeholder={lang === "ar" ? "عنوان القرار" : "Title"} value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} />
                <Input data-testid="kn-f-year" placeholder={lang === "ar" ? "السنة (هجري)" : "Year"} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
                <Input data-testid="kn-f-basis" placeholder={lang === "ar" ? "الأسانيد (يفصلها فاصلة)" : "Basis (comma)"} value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value })} />
                <Input data-testid="kn-f-url" placeholder={lang === "ar" ? "رابط المصدر" : "Source URL"} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
                <Input data-testid="kn-f-summary" placeholder={lang === "ar" ? "ملخص القرار" : "Summary"} value={form.summary_ar} onChange={(e) => setForm({ ...form, summary_ar: e.target.value })} className="sm:col-span-2" />
              </div>
              <Button data-testid="kn-add-btn" onClick={submitDecision} className="mt-3 rounded-full bg-[hsl(217_63%_15%)]">{t("kn_save")}</Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Input data-testid="kn-search" placeholder={t("kn_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
            <Select value={tax} onValueChange={setTax}>
              <SelectTrigger data-testid="kn-tax" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("kn_all_tax")}</SelectItem>
                <SelectItem value="vat">{t("tk_tax_vat")}</SelectItem>
                <SelectItem value="excise">{t("tk_tax_excise")}</SelectItem>
                <SelectItem value="rett">{t("tk_tax_rett")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={committee} onValueChange={setCommittee}>
              <SelectTrigger data-testid="kn-committee" className="max-w-xs"><SelectValue placeholder={t("all_committees")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all_committees")}</SelectItem>
                {data.committees.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dtype} onValueChange={setDtype}>
              <SelectTrigger data-testid="kn-dtype" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === "ar" ? "الكل" : "All"}</SelectItem>
                <SelectItem value="substantive">{t("dtype_substantive")}</SelectItem>
                <SelectItem value="procedural">{t("dtype_procedural")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {data.decisions.length === 0 && <div className="text-sm text-muted-foreground">—</div>}
            {data.decisions.map((d) => (
              <div key={d.reference} data-testid={`decision-${d.reference}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-mono text-xs font-bold text-[hsl(217_63%_15%)]">{d.reference}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.type === "substantive" ? "bg-[hsl(158_64%_92%)] text-[hsl(158_64%_26%)]" : "bg-[hsl(38_92%_90%)] text-[hsl(30_80%_38%)]"}`}>{d.type === "substantive" ? t("dtype_substantive") : t("dtype_procedural")}</span>
                </div>
                <div className="font-semibold text-sm text-[hsl(217_63%_15%)]">{lang === "ar" ? d.title_ar : d.title_en}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{d.committee} · {d.date_hijri}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{lang === "ar" ? d.summary_ar : d.summary_en}</p>
                <div className="mt-3 text-xs">
                  <div className="font-medium text-foreground">{t("decision_basis")}:</div>
                  <ul className="text-muted-foreground mt-1 space-y-0.5">{d.basis.map((b, i) => <li key={i}>• {b}</li>)}</ul>
                </div>
                <div className="mt-2 text-xs"><span className="font-medium text-foreground">{t("decision_outcome")}: </span><span className="text-muted-foreground">{lang === "ar" ? d.outcome_ar : d.outcome_en}</span></div>
                <a href={d.url} target="_blank" rel="noreferrer" data-testid={`decision-src-${d.reference}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(158_64%_28%)] hover:underline">
                  <ExternalLink className="h-3.5 w-3.5" /> {t("official_source")}
                </a>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="articles" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {articles.map((a) => (
              <div key={a.id} data-testid={`article-${a.id}`} className="rounded-2xl border border-border bg-card p-5">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{a.tag}</span>
                <div className="font-semibold text-[hsl(217_63%_15%)] mt-2">{lang === "ar" ? a.title_ar : a.title_en}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{lang === "ar" ? a.body_ar : a.body_en}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
